import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, SelectChangeEvent, useTheme,  } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';

import CoreSelect, { SelectOption } from '../ui/core/CoreSelect';
import { authService } from '../../services/authService';
import { websocketService } from '../../services/websocketService';

interface ChangeTenantDialogProps {
    isOpen: boolean;
    tenantOptions: SelectOption[];
    tenant: string | undefined
    onClose: () => void;
}

const ChangeTenantDialog: FC<ChangeTenantDialogProps> = ({ isOpen, tenantOptions, tenant, onClose }) => {
    const theme = useTheme();

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [currentTenant, setCurrentTenant] = useState<string | null>(null);
    const [isOperationInProgress, setIsOperationInProgress] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);  // Keep form disabled while redirecting

    // Companies/Tenants in alphabetical order (case-insensitive), without mutating the prop
    const sortedTenantOptions = useMemo(
        () => [...tenantOptions].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
        [tenantOptions],
    );

    useEffect(()=> {
        if(isOpen && tenant){
            setCurrentTenant(tenant)
        }
    }, [isOpen, tenant])

    const handleClose = () => {
        onClose()
        setCurrentTenant(null);
        setIsOperationInProgress(false);
        setErrorMessage('')
        setSuccessMessage('')
        setIsRedirecting(false)
    }

    const handleOnChangeTentant = async () => {
        setIsOperationInProgress(true)
        setErrorMessage('')
        setSuccessMessage('')

        try {
            const successResult = await authService.changeTenant(currentTenant as string);
            if (!successResult) {
                throw new Error('Failed to change company');
            }

            websocketService.disconnect();
            await websocketService.connectUser();

            setSuccessMessage('Company changed successfully. Reloading page...');
            setIsOperationInProgress(false);
            setIsRedirecting(true)
            setTimeout(() => {
                window.location.assign('/');
            }, 1500);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to change company');
            setIsOperationInProgress(false);
            setIsRedirecting(false)
        }
    }

    const handleOnTenantChange = (event: SelectChangeEvent<unknown>) => {
        setCurrentTenant(event.target.value as string);
    }

    const handleKeyDown = React.useCallback((e: KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            handleOnChangeTentant();
        }
      }, [currentTenant]);
    
      React.useEffect(() => {
        if (!isOpen) return;
        const listener = (e: KeyboardEvent) => handleKeyDown(e);
        document.addEventListener("keydown", listener, true);
        return () => document.removeEventListener("keydown", listener, true);
      }, [isOpen, handleKeyDown]);

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth='xs'
            fullWidth
            PaperProps={{
            sx: {
                borderRadius: 2,
            },
            }}
        >
            <DialogTitle
                sx={{
                    pb: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontSize: "19px",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                }}
            >
                Change Company
            </DialogTitle>
    
            <DialogContent sx={{ px: 3, py: 2 }}>
                <Box sx={{ paddingTop: 2}}>
                    <CoreSelect
                        label="Company"
                        options={sortedTenantOptions}
                        value={currentTenant}
                        disabled={isOperationInProgress || isRedirecting}
                        onChange={handleOnTenantChange}
                        onKeyDown={(e) => e.preventDefault()}
                    />

                    {errorMessage && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {errorMessage}
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            {successMessage}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
    
            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    gap: 1.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}>
                <Button
                    onClick={handleClose}
                    disabled={isOperationInProgress || isRedirecting}
                    variant="outlined"
                >
                    Cancel
                </Button>
        
                <Button
                    onClick={handleOnChangeTentant}
                    disabled={isOperationInProgress || isRedirecting}
                    variant="contained"
                    loading={isOperationInProgress}
                >
                    Change
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeTenantDialog;